import { Injectable, OnDestroy } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  getAccountSelectionStorageKey,
  isSpecificTenant,
  resolveAccountForTenant,
  readStoredAccountHomeId,
} from './account-selection.util';
import { SESSION_STORAGE_KEYS } from '../session_storage/constants/session-storage-keys.constant';

export interface OrganizationIdentity {
  tenantId: string;
  homeAccountId: string;
  organizationId: string | null;
}

export interface OrganizationContext {
  phase: 'bootstrap' | 'ready' | 'switching' | 'stale' | 'failed';
  identity: OrganizationIdentity | null;
  pendingTenantId: string | null;
  generation: number;
}

/** Tab-local runtime truth. Storage is a preference, not live request authority. */
@Injectable({ providedIn: 'root' })
export class OrganizationContextService implements OnDestroy {
  private readonly state = new BehaviorSubject<OrganizationContext>({
    phase: 'bootstrap',
    identity: null,
    pendingTenantId: null,
    generation: 0,
  });
  private readonly invalidation = new Subject<void>();
  readonly state$ = this.state.asObservable();
  readonly invalidated$ = this.invalidation.asObservable();
  private readonly onStorage = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage) return;
    if (event.key === null || event.key === SESSION_STORAGE_KEYS.TENANT_ID || event.key?.startsWith('AccountHomeId:')) {
      const identity = this.snapshot.identity;
      // Storage events originate in another tab. Even during validation we must
      // not overwrite that tab's new selection when our pending request returns.
      if (
        this.snapshot.phase === 'switching' ||
        (identity &&
          event.key === getAccountSelectionStorageKey(identity.tenantId) &&
          readStoredAccountHomeId(identity.tenantId) !== identity.homeAccountId)
      ) {
        this.transition({ ...this.snapshot, phase: 'stale', pendingTenantId: null });
        return;
      }
      this.checkPreferenceConsistency();
    }
  };

  constructor() {
    window.addEventListener('storage', this.onStorage);
  }
  get snapshot(): OrganizationContext {
    return this.state.value;
  }

  isStale(): boolean {
    return this.snapshot.phase === 'stale';
  }

  /** Same restoration policy for the root interceptor and configuration loader. */
  restoreSession(accounts: AccountInfo[], active: AccountInfo | null): void {
    if (this.snapshot.phase !== 'bootstrap') return;
    const tenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
    if (!isSpecificTenant(tenantId)) return;
    const account = resolveAccountForTenant(accounts, active, tenantId);
    if (account) this.confirm(account);
  }

  failSwitch(generation: number): void {
    if (this.snapshot.generation !== generation || this.snapshot.phase !== 'switching') return;
    this.transition({ ...this.snapshot, phase: 'failed', identity: null, pendingTenantId: null });
  }

  beginSwitch(tenantId: string): OrganizationContext {
    this.checkPreferenceConsistency();
    if (this.snapshot.phase === 'switching' || this.snapshot.phase === 'stale') {
      throw new Error('Organization context is not available for switching');
    }
    const previous = this.snapshot;
    this.transition({ ...previous, phase: 'switching', pendingTenantId: tenantId });
    return previous;
  }

  restore(previous: OrganizationContext): void {
    // Never resurrect an old context after another tab has changed the preference.
    if (this.snapshot.phase === 'stale') return;
    this.transition({ ...previous, pendingTenantId: null });
    this.checkPreferenceConsistency();
  }

  confirm(account: AccountInfo, organizationId: string | null = null): void {
    if (this.isStale()) throw new Error('Organization context changed in another tab');
    const identity = { tenantId: account.tenantId, homeAccountId: account.homeAccountId, organizationId };
    const current = this.snapshot;
    const sameAccount =
      current.identity?.tenantId === identity.tenantId && current.identity?.homeAccountId === identity.homeAccountId;
    if (sameAccount && current.phase === 'ready' && !organizationId) {
      identity.organizationId = current.identity.organizationId;
    }
    const next = { ...current, identity, phase: 'ready' as const, pendingTenantId: null };
    if (sameAccount && current.phase === 'ready') this.state.next(next);
    else this.transition(next);
  }

  bindOrganization(organizationId: string): void {
    if (this.snapshot.identity && this.snapshot.phase === 'ready') {
      this.state.next({ ...this.snapshot, identity: { ...this.snapshot.identity, organizationId } });
    }
  }

  clear(): void {
    this.transition({ ...this.snapshot, phase: 'bootstrap', identity: null, pendingTenantId: null });
  }

  resolveAccount(accounts: AccountInfo[], active: AccountInfo | null): AccountInfo | null {
    this.assertRequestAllowed();
    const identity = this.snapshot.identity;
    if (!identity)
      return resolveAccountForTenant(accounts, active, localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID));
    return (
      accounts.find(
        (account) => account.tenantId === identity.tenantId && account.homeAccountId === identity.homeAccountId,
      ) ?? null
    );
  }

  assertRequestAllowed(): void {
    this.checkPreferenceConsistency();
    if (['switching', 'stale', 'failed'].includes(this.snapshot.phase)) {
      throw new Error('Organization context changed; request cancelled');
    }
  }

  private checkPreferenceConsistency(): void {
    const { identity, phase } = this.snapshot;
    if (!identity || phase === 'switching' || phase === 'stale') return;
    const homeAccountId = readStoredAccountHomeId(identity.tenantId);
    if (
      localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID) !== identity.tenantId ||
      (homeAccountId !== null && homeAccountId !== identity.homeAccountId)
    ) {
      this.transition({ ...this.snapshot, phase: 'stale', pendingTenantId: null });
    }
  }

  private transition(next: OrganizationContext): void {
    const generation = this.snapshot.generation + 1;
    // Close the request gate before clearing caches; publish ready only after
    // invalidation, so new requests from ready subscribers are not cancelled.
    this.state.next({ ...next, phase: next.phase === 'stale' ? 'stale' : 'switching', generation });
    this.invalidation.next();
    this.state.next({ ...next, generation });
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorage);
    this.invalidation.complete();
    this.state.complete();
  }
}
